import { useEffect, useState, Fragment } from 'react';
import Swal from 'sweetalert2';
import api from './api';
import { ROLES, roleLabel, roleBadgeStyle } from './roles';

const ALL_ROLES = [ROLES.USER, ROLES.RESELLER, ROLES.ADMIN];
const emptyForm = { Username: '', Password: '', Name: '', Role: ROLES.USER, Email: '', Mobile: '' };


export default function AdminPage({ onBack, onLogout, onConnect }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(null);

  const copyPassword = () => {
    navigator.clipboard.writeText(form.Password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userVms, setUserVms] = useState({});
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('users');
  const [hosts, setHosts] = useState([]);
  const [hostsLoading, setHostsLoading] = useState(false);
  const [hostsError, setHostsError] = useState(null);
  const [hostSearch, setHostSearch] = useState('');
  const [expandedHostId, setExpandedHostId] = useState(null);
  const [hostVms, setHostVms] = useState({});
  const [assigning, setAssigning] = useState(null); // { hostId, vmId }
  const [assignUserId, setAssignUserId] = useState('');
  const [assignUserLabel, setAssignUserLabel] = useState('');
  const [assignSearch, setAssignSearch] = useState('');
  const [assignDropdownOpen, setAssignDropdownOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [assignSuccess, setAssignSuccess] = useState(false);

  const name = sessionStorage.getItem('auth_name') || 'User';

  const loadUsers = (q = search) => {
    setLoading(true);
    api.get('/Admin/User/List', { params: q.trim() ? { Search: q.trim() } : {} })
      .then(res => setUsers(res.data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers(search);
  };

  const loadHosts = (q = hostSearch) => {
    setHostsLoading(true);
    api.get('/Admin/Host/List', { params: q.trim() ? { Search: q.trim() } : {} })
      .then(res => setHosts(res.data))
      .catch(err => setHostsError(err.message))
      .finally(() => setHostsLoading(false));
  };

  const handleTabChange = (t) => {
    setTab(t);
    if (t === 'hosts' && hosts.length === 0 && !hostsLoading) loadHosts();
  };

  const toggleHostVms = (hostId) => {
    if (expandedHostId === hostId) { setExpandedHostId(null); return; }
    setExpandedHostId(hostId);
    if (users.length === 0) loadUsers();
    if (!hostVms[hostId]) {
      setHostVms(v => ({ ...v, [hostId]: { loading: true, data: [], error: null } }));
      api.get(`/Admin/Host/${hostId}/GetVM`)
        .then(res => setHostVms(v => ({ ...v, [hostId]: { loading: false, data: res.data, error: null } })))
        .catch(err => setHostVms(v => ({ ...v, [hostId]: { loading: false, data: [], error: err.message } })));
    }
  };

  const openAssign = (hostId, vmId) => {
    setAssigning({ hostId, vmId });
    setAssignUserId('');
    setAssignUserLabel('');
    setAssignSearch('');
    setAssignDropdownOpen(false);
    setAssignError(null);
    setAssignSuccess(false);
  };

  const submitAssign = async () => {
    if (!assignUserId) return;
    setAssignLoading(true);
    setAssignError(null);
    setAssignSuccess(false);
    try {
      await api.put(`/Admin/User/VM/${assignUserId}/Create/${assigning.hostId}/${assigning.vmId}`);
      setAssignSuccess(true);
      setTimeout(() => setAssigning(null), 1200);
    } catch (err) {
      setAssignError(err.response?.data?.message ?? err.message);
    } finally {
      setAssignLoading(false);
    }
  };

  const set = (field) => (e) => {
    setFormError(null);
    setFormSuccess(false);
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const toggleUserVms = (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    if (!userVms[userId]) {
      setUserVms(v => ({ ...v, [userId]: { loading: true, data: [], error: null } }));
      api.get(`/Admin/User/VM/${userId}`)
        .then(res => setUserVms(v => ({ ...v, [userId]: { loading: false, data: res.data, error: null } })))
        .catch(err => setUserVms(v => ({ ...v, [userId]: { loading: false, data: [], error: err.message } })));
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    setFormSuccess(false);
    try {
      const fd = new FormData();
      fd.append('Username', form.Username);
      fd.append('Password', form.Password);
      fd.append('Name', form.Name);
      fd.append('Role', form.Role);
      fd.append('Email', form.Email);
      fd.append('Mobile', form.Mobile);
      await api.post('/Admin/User/Create', fd);
      setFormSuccess(true);
      setForm(emptyForm);
      loadUsers();
    } catch (err) {
      setFormError(err.response?.data?.message ?? err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#1a1a1a', color: '#e5e7eb', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '12px 24px',
        background: '#2d2d2d', borderBottom: '1px solid #444',
      }}>
        <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>Console Management</span>
        <div style={{ flex: 1 }} />
        <button onClick={onBack} style={{
          padding: '4px 12px', borderRadius: 6, border: 'none',
          background: '#374151', color: '#d1d5db', fontSize: 12,
          cursor: 'pointer', marginRight: 8,
        }}>
          ← Back
        </button>
        <span style={{ fontSize: 13, color: '#9ca3af', marginRight: 16 }}>{name}</span>
        <button onClick={async () => { try { await api.get('/Authentication/Logout'); } finally { onLogout(); } }} style={{
          padding: '4px 12px', borderRadius: 6, border: 'none',
          background: '#374151', color: '#d1d5db', fontSize: 12,
          cursor: 'pointer',
        }}>
          Logout
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '32px 24px', maxWidth: 900, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #374151' }}>
          {['users', 'hosts'].map(t => (
            <button key={t} onClick={() => handleTabChange(t)} style={{
              padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 13, borderBottom: tab === t ? '2px solid #93c5fd' : '2px solid transparent',
              color: tab === t ? '#93c5fd' : '#6b7280', marginBottom: -1,
            }}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {tab === 'users' && <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#f9fafb' }}>Users</h2>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6 }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              style={{ ...inputStyle, padding: '4px 10px', fontSize: 12, width: 180 }}
            />
            <button type="submit" style={{
              padding: '4px 12px', borderRadius: 6, border: 'none',
              background: '#374151', color: '#d1d5db', fontSize: 12, cursor: 'pointer',
            }}>Search</button>
            {search && (
              <button type="button" onClick={() => { setSearch(''); loadUsers(''); }} style={{
                padding: '4px 10px', borderRadius: 6, border: 'none',
                background: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer',
              }}>✕</button>
            )}
          </form>
          <div style={{ flex: 1 }} />
          <button onClick={() => { setShowForm(v => !v); setFormError(null); setFormSuccess(false); }} style={{
            padding: '6px 14px', borderRadius: 6, border: 'none',
            background: '#1e3a5f', color: '#93c5fd', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>
            {showForm ? 'Cancel' : '+ Add User'}
          </button>
        </div>}

        {tab === 'users' && showForm && (
          <form onSubmit={addUser} style={{
            background: '#2d2d2d', border: '1px solid #3f3f46', borderRadius: 10,
            padding: '24px', marginBottom: 24,
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
          }}>
            {formSuccess && (
              <div style={{ gridColumn: '1/-1', background: '#14532d', color: '#bbf7d0', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
                User created successfully.
              </div>
            )}
            {formError && (
              <div style={{ gridColumn: '1/-1', background: '#7f1d1d', color: '#fecaca', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
                {formError}
              </div>
            )}
            <label style={labelStyle}>
              <span style={labelText}>Username *</span>
              <input required value={form.Username} onChange={set('Username')} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={labelText}>Password *</span>
                <button type="button" onClick={() => {
                  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
                  const pwd = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
                  setForm(f => ({ ...f, Password: pwd }));
                  setShowPassword(true);
                }} style={{
                  background: 'none', border: '1px solid #555', borderRadius: 4,
                  color: '#9ca3af', fontSize: 10, cursor: 'pointer', padding: '1px 6px',
                }}>
                  Random
                </button>
                <button type="button" onClick={copyPassword} disabled={!form.Password} style={{
                  background: copied ? '#14532d' : 'none', border: `1px solid ${copied ? '#166534' : '#555'}`, borderRadius: 4,
                  color: copied ? '#bbf7d0' : form.Password ? '#9ca3af' : '#4b5563',
                  fontSize: 10, cursor: form.Password ? 'pointer' : 'not-allowed', padding: '1px 6px',
                  transition: 'all 0.2s',
                }}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={form.Password}
                  onChange={set('Password')}
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', paddingRight: 60 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', color: '#6b7280', fontSize: 11,
                    cursor: 'pointer', padding: '2px 4px',
                  }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </label>
            <label style={labelStyle}>
              <span style={labelText}>Name</span>
              <input value={form.Name} onChange={set('Name')} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              <span style={labelText}>Role</span>
              <select value={form.Role} onChange={set('Role')} style={inputStyle}>
                {ALL_ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </label>
            <label style={labelStyle}>
              <span style={labelText}>Email</span>
              <input type="email" value={form.Email} onChange={set('Email')} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              <span style={labelText}>Mobile</span>
              <input value={form.Mobile} onChange={set('Mobile')} style={inputStyle} />
            </label>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button type="submit" disabled={submitting} style={{
                width: '100%', padding: '9px', borderRadius: 6, border: 'none',
                background: submitting ? '#1e3a5f88' : '#1e3a5f',
                color: '#93c5fd', fontWeight: 600, fontSize: 13,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}>
                {submitting ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        )}


        {tab === 'users' && loading && <div style={{ color: '#9ca3af' }}>Loading...</div>}
        {tab === 'users' && error && <div style={{ color: '#fca5a5' }}>Error: {error}</div>}

        {tab === 'users' && !loading && !error && users.length === 0 && (
          <div style={{ color: '#6b7280' }}>No users found.</div>
        )}

        {tab === 'users' && !loading && !error && users.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: '#6b7280', textAlign: 'left', borderBottom: '1px solid #374151' }}>
                <th style={{ padding: '8px 12px' }}>Username</th>
                <th style={{ padding: '8px 12px' }}>Name</th>
                <th style={{ padding: '8px 12px' }}>Email</th>
                <th style={{ padding: '8px 12px' }}>Mobile</th>
                <th style={{ padding: '8px 12px' }}>Level</th>
                <th style={{ padding: '8px 12px' }}>Status</th>
                <th style={{ padding: '8px 12px' }}></th>
                <th style={{ padding: '8px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <Fragment key={u.user_id}>
                  <tr style={{ borderBottom: '1px solid #1f2937', color: '#d1d5db' }}>
                    <td style={{ padding: '8px 12px' }}>{u.username || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{u.name || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{u.email || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{u.mobile || '—'}</td>
                    <td style={{ padding: '8px 12px' }}><span style={roleBadgeStyle(u.level)}>{roleLabel(u.level)}</span></td>
                    <td style={{ padding: '8px 12px' }}>
                      <button
                        disabled={togglingStatus === u.user_id}
                        onClick={async () => {
                          const newStatus = u.status === 1 ? 0 : 1;
                          const label = newStatus === 1 ? 'Enable' : 'Disable';
                          const result = await Swal.fire({
                            title: `${label} User?`,
                            text: `${label} "${u.username}"?`,
                            icon: 'question',
                            showCancelButton: true,
                            confirmButtonColor: newStatus === 1 ? '#14532d' : '#7f1d1d',
                            cancelButtonColor: '#374151',
                            confirmButtonText: label,
                            background: '#1f2937',
                            color: '#f9fafb',
                          });
                          if (!result.isConfirmed) return;
                          setTogglingStatus(u.user_id);
                          try {
                            const res = await api.post(`/Admin/User/${u.user_id}/Status`, null, { params: { Status: newStatus } });
                            if (res.data.status) {
                              setUsers(prev => prev.map(x => x.user_id === u.user_id ? { ...x, status: newStatus } : x));
                            } else {
                              Swal.fire({ title: 'Failed', icon: 'error', background: '#1f2937', color: '#f9fafb' });
                            }
                          } catch (err) {
                            Swal.fire({ title: 'Error', text: err.response?.data?.message ?? err.message, icon: 'error', background: '#1f2937', color: '#f9fafb' });
                          } finally {
                            setTogglingStatus(null);
                          }
                        }}
                        style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                          background: u.status === 1 ? '#14532d' : '#7f1d1d',
                          color: u.status === 1 ? '#bbf7d0' : '#fecaca',
                          border: 'none', cursor: togglingStatus === u.user_id ? 'not-allowed' : 'pointer',
                          opacity: togglingStatus === u.user_id ? 0.6 : 1,
                        }}
                      >
                        {togglingStatus === u.user_id ? '...' : u.status === 1 ? 'Enabled' : 'Disabled'}
                      </button>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <button onClick={() => toggleUserVms(u.user_id)} style={{
                        padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11,
                        background: expandedUserId === u.user_id ? '#374151' : '#1e3a5f',
                        color: expandedUserId === u.user_id ? '#d1d5db' : '#93c5fd',
                        cursor: 'pointer',
                      }}>
                        {expandedUserId === u.user_id ? '▲ VMs' : '▼ VMs'}
                      </button>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <button onClick={async () => {
                        const result = await Swal.fire({
                          title: 'Reset Password?',
                          text: `Reset password for "${u.username}"?`,
                          icon: 'warning',
                          showCancelButton: true,
                          confirmButtonColor: '#1e3a5f',
                          cancelButtonColor: '#374151',
                          confirmButtonText: 'Reset',
                          background: '#1f2937',
                          color: '#f9fafb',
                        });
                        if (!result.isConfirmed) return;
                        try {
                          const res = await api.post(`/Admin/User/${u.user_id}/ResetPassword`);
                          if (!res.data.status) { Swal.fire({ title: 'Failed', icon: 'error', background: '#1f2937', color: '#f9fafb' }); return; }
                          Swal.fire({
                            title: 'Password Reset',
                            html: `<div style="text-align:left;font-size:13px;color:#d1d5db">
                              New password:<br/>
                              <div style="display:flex;align-items:center;gap:8px;margin-top:8px">
                                <code style="background:#111827;padding:6px 12px;border-radius:6px;font-size:14px;color:#93c5fd;flex:1">${res.data.password}</code>
                                <button onclick="navigator.clipboard.writeText('${res.data.password}');this.textContent='Copied!';setTimeout(()=>this.textContent='Copy',1500)" style="padding:4px 10px;border-radius:4px;border:none;background:#374151;color:#d1d5db;cursor:pointer;font-size:12px">Copy</button>
                              </div>
                            </div>`,
                            background: '#1f2937',
                            color: '#f9fafb',
                            confirmButtonColor: '#374151',
                          });
                        } catch (err) {
                          Swal.fire({ title: 'Error', text: err.response?.data?.message ?? err.message, icon: 'error', background: '#1f2937', color: '#f9fafb' });
                        }
                      }} style={{
                        padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11,
                        background: '#1c2a3a', color: '#7dd3fc', cursor: 'pointer', fontWeight: 600,
                      }}>
                        Reset PW
                      </button>
                    </td>
                  </tr>
                  {expandedUserId === u.user_id && (
                    <tr>
                      <td colSpan={7} style={{ padding: '0 12px 12px 32px', background: '#111827' }}>
                        {userVms[u.user_id]?.loading && <div style={{ color: '#9ca3af', padding: '8px 0', fontSize: 12 }}>Loading VMs...</div>}
                        {userVms[u.user_id]?.error && <div style={{ color: '#fca5a5', padding: '8px 0', fontSize: 12 }}>{userVms[u.user_id].error}</div>}
                        {!userVms[u.user_id]?.loading && !userVms[u.user_id]?.error && userVms[u.user_id]?.data.length === 0 && (
                          <div style={{ color: '#6b7280', padding: '8px 0', fontSize: 12 }}>No VMs.</div>
                        )}
                        {userVms[u.user_id]?.data.length > 0 && (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                            <thead>
                              <tr style={{ color: '#6b7280', textAlign: 'left', borderBottom: '1px solid #1f2937' }}>
                                <th style={{ padding: '6px 10px' }}>Name</th>
                                <th style={{ padding: '6px 10px' }}>IP</th>
                                <th style={{ padding: '6px 10px' }}>MAC</th>
                                <th style={{ padding: '6px 10px' }}>State</th>
                                <th style={{ padding: '6px 10px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {userVms[u.user_id].data.map(vm => (
                                <tr key={vm.vmid} style={{ borderBottom: '1px solid #1f2937', color: '#d1d5db' }}>
                                  <td style={{ padding: '6px 10px' }}>{vm.name || '—'}</td>
                                  <td style={{ padding: '6px 10px' }}>{vm.ip || '—'}</td>
                                  <td style={{ padding: '6px 10px' }}>{vm.mac_address || '—'}</td>
                                  <td style={{ padding: '6px 10px' }}>
                                    <span style={{
                                      padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                                      background: vm.state === 1 ? '#14532d' : '#374151',
                                      color: vm.state === 1 ? '#bbf7d0' : '#9ca3af',
                                    }}>
                                      {vm.state === 1 ? 'Running' : 'Stopped'}
                                    </span>
                                  </td>
                                  <td style={{ padding: '6px 10px' }}>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                      <button onClick={() => onConnect(vm)} style={{
                                        padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11,
                                        background: '#1e3a5f', color: '#93c5fd', cursor: 'pointer', fontWeight: 600,
                                      }}>
                                        Console
                                      </button>
                                      <button onClick={async () => {
                                        const { isConfirmed } = await Swal.fire({
                                          title: 'Start VM?', text: `Start "${vm.name || vm.vmid}"?`, icon: 'question',
                                          showCancelButton: true, confirmButtonColor: '#14532d', cancelButtonColor: '#374151',
                                          confirmButtonText: 'Start', background: '#1f2937', color: '#f9fafb',
                                        });
                                        if (!isConfirmed) return;
                                        try {
                                          await api.put(`/Admin/VM/${vm.vmid}/Start`);
                                          Swal.fire({ title: 'Started', icon: 'success', timer: 1200, showConfirmButton: false, background: '#1f2937', color: '#f9fafb' });
                                        } catch (err) {
                                          Swal.fire({ title: 'Error', text: err.response?.data?.message ?? err.message, icon: 'error', background: '#1f2937', color: '#f9fafb' });
                                        }
                                      }} style={{
                                        padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11,
                                        background: '#14532d', color: '#bbf7d0', cursor: 'pointer', fontWeight: 600,
                                      }}>
                                        Start
                                      </button>
                                      <button onClick={async () => {
                                        const { isConfirmed } = await Swal.fire({
                                          title: 'Stop VM?', text: `Stop "${vm.name || vm.vmid}"?`, icon: 'warning',
                                          showCancelButton: true, confirmButtonColor: '#7f1d1d', cancelButtonColor: '#374151',
                                          confirmButtonText: 'Stop', background: '#1f2937', color: '#f9fafb',
                                        });
                                        if (!isConfirmed) return;
                                        try {
                                          await api.put(`/Admin/VM/${vm.vmid}/Stop`);
                                          Swal.fire({ title: 'Stopped', icon: 'success', timer: 1200, showConfirmButton: false, background: '#1f2937', color: '#f9fafb' });
                                        } catch (err) {
                                          Swal.fire({ title: 'Error', text: err.response?.data?.message ?? err.message, icon: 'error', background: '#1f2937', color: '#f9fafb' });
                                        }
                                      }} style={{
                                        padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11,
                                        background: '#3b1a1a', color: '#fca5a5', cursor: 'pointer', fontWeight: 600,
                                      }}>
                                        Stop
                                      </button>
                                      <button onClick={async () => {
                                        const { isConfirmed } = await Swal.fire({
                                          title: 'Reboot VM?', text: `Reboot "${vm.name || vm.vmid}"?`, icon: 'warning',
                                          showCancelButton: true, confirmButtonColor: '#92400e', cancelButtonColor: '#374151',
                                          confirmButtonText: 'Reboot', background: '#1f2937', color: '#f9fafb',
                                        });
                                        if (!isConfirmed) return;
                                        try {
                                          await api.put(`/Admin/VM/${vm.vmid}/Reboot`);
                                          Swal.fire({ title: 'Reboot initiated', icon: 'success', timer: 1200, showConfirmButton: false, background: '#1f2937', color: '#f9fafb' });
                                        } catch (err) {
                                          Swal.fire({ title: 'Error', text: err.response?.data?.message ?? err.message, icon: 'error', background: '#1f2937', color: '#f9fafb' });
                                        }
                                      }} style={{
                                        padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11,
                                        background: '#451a03', color: '#fb923c', cursor: 'pointer', fontWeight: 600,
                                      }}>
                                        Reboot
                                      </button>
                                    </div>
                                  </td>
                                  <td style={{ padding: '6px 10px' }}>
                                    <button onClick={async () => {
                                      const result = await Swal.fire({
                                        title: 'Remove VM?',
                                        text: `Remove "${vm.name || vm.vmid}" from this user?`,
                                        icon: 'warning',
                                        showCancelButton: true,
                                        confirmButtonColor: '#7f1d1d',
                                        cancelButtonColor: '#374151',
                                        confirmButtonText: 'Remove',
                                        background: '#1f2937',
                                        color: '#f9fafb',
                                      });
                                      if (!result.isConfirmed) return;
                                      api.delete(`/Admin/User/VM/${vm.vmid}`)
                                        .then(() => {
                                          setUserVms(v => ({
                                            ...v,
                                            [u.user_id]: { ...v[u.user_id], data: v[u.user_id].data.filter(x => x.vmid !== vm.vmid) },
                                          }));
                                          Swal.fire({ title: 'Removed', icon: 'success', timer: 1200, showConfirmButton: false, background: '#1f2937', color: '#f9fafb' });
                                        })
                                        .catch(err => Swal.fire({ title: 'Error', text: err.response?.data?.message ?? err.message, icon: 'error', background: '#1f2937', color: '#f9fafb' }));
                                    }} style={{
                                      padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11,
                                      background: '#3b1a1a', color: '#fca5a5', cursor: 'pointer', fontWeight: 600,
                                    }}>
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}

        {/* Hosts Tab */}
        {tab === 'hosts' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#f9fafb' }}>Hosts</h2>
              <form onSubmit={(e) => { e.preventDefault(); loadHosts(hostSearch); }} style={{ display: 'flex', gap: 6 }}>
                <input
                  value={hostSearch}
                  onChange={e => setHostSearch(e.target.value)}
                  placeholder="Search..."
                  style={{ ...inputStyle, padding: '4px 10px', fontSize: 12, width: 180 }}
                />
                <button type="submit" style={{
                  padding: '4px 12px', borderRadius: 6, border: 'none',
                  background: '#374151', color: '#d1d5db', fontSize: 12, cursor: 'pointer',
                }}>Search</button>
                {hostSearch && (
                  <button type="button" onClick={() => { setHostSearch(''); loadHosts(''); }} style={{
                    padding: '4px 10px', borderRadius: 6, border: 'none',
                    background: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer',
                  }}>✕</button>
                )}
              </form>
            </div>

            {hostsLoading && <div style={{ color: '#9ca3af' }}>Loading...</div>}
            {hostsError && <div style={{ color: '#fca5a5' }}>Error: {hostsError}</div>}
            {!hostsLoading && !hostsError && hosts.length === 0 && (
              <div style={{ color: '#6b7280' }}>No hosts found.</div>
            )}
            {!hostsLoading && !hostsError && hosts.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ color: '#6b7280', textAlign: 'left', borderBottom: '1px solid #374151' }}>
                    <th style={{ padding: '8px 12px' }}>Name</th>
                    <th style={{ padding: '8px 12px' }}>IP</th>
                    <th style={{ padding: '8px 12px' }}>Port</th>
                    <th style={{ padding: '8px 12px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {hosts.map(h => (
                    <Fragment key={h.host_id}>
                      <tr style={{ borderBottom: '1px solid #1f2937', color: '#d1d5db' }}>
                        <td style={{ padding: '8px 12px' }}>{h.host_name || '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{h.host_ip || '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{h.host_port}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <button onClick={() => toggleHostVms(h.host_id)} style={{
                            padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11,
                            background: expandedHostId === h.host_id ? '#374151' : '#1e3a5f',
                            color: expandedHostId === h.host_id ? '#d1d5db' : '#93c5fd',
                            cursor: 'pointer',
                          }}>
                            {expandedHostId === h.host_id ? '▲ VMs' : '▼ VMs'}
                          </button>
                        </td>
                      </tr>
                      {expandedHostId === h.host_id && (
                        <tr>
                          <td colSpan={4} style={{ padding: '0 12px 12px 32px', background: '#111827' }}>
                            {hostVms[h.host_id]?.loading && <div style={{ color: '#9ca3af', padding: '8px 0', fontSize: 12 }}>Loading VMs...</div>}
                            {hostVms[h.host_id]?.error && <div style={{ color: '#fca5a5', padding: '8px 0', fontSize: 12 }}>{hostVms[h.host_id].error}</div>}
                            {!hostVms[h.host_id]?.loading && !hostVms[h.host_id]?.error && hostVms[h.host_id]?.data.length === 0 && (
                              <div style={{ color: '#6b7280', padding: '8px 0', fontSize: 12 }}>No VMs.</div>
                            )}
                            {hostVms[h.host_id]?.data.length > 0 && (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                                <thead>
                                  <tr style={{ color: '#6b7280', textAlign: 'left', borderBottom: '1px solid #1f2937' }}>
                                    <th style={{ padding: '6px 10px' }}>ID</th>
                                    <th style={{ padding: '6px 10px' }}>Name</th>
                                    <th style={{ padding: '6px 10px' }}>Node</th>
                                    <th style={{ padding: '6px 10px' }}>CPUs</th>
                                    <th style={{ padding: '6px 10px' }}>Memory</th>
                                    <th style={{ padding: '6px 10px' }}>Status</th>
                                    <th style={{ padding: '6px 10px' }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {hostVms[h.host_id].data.map(vm => (
                                    <Fragment key={vm.vm_id}>
                                      <tr style={{ borderBottom: '1px solid #1f2937', color: '#d1d5db' }}>
                                        <td style={{ padding: '6px 10px' }}>{vm.vm_id}</td>
                                        <td style={{ padding: '6px 10px' }}>{vm.name || '—'}</td>
                                        <td style={{ padding: '6px 10px' }}>{vm.node || '—'}</td>
                                        <td style={{ padding: '6px 10px' }}>{vm.cpus}</td>
                                        <td style={{ padding: '6px 10px' }}>{vm.memory ? `${Math.round(vm.memory / 1024 / 1024)} MB` : '—'}</td>
                                        <td style={{ padding: '6px 10px' }}>
                                          <span style={{
                                            padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                                            background: vm.status === 'running' ? '#14532d' : '#374151',
                                            color: vm.status === 'running' ? '#bbf7d0' : '#9ca3af',
                                          }}>
                                            {vm.status || '—'}
                                          </span>
                                        </td>
                                        <td style={{ padding: '6px 10px' }}>
                                          <button onClick={() => openAssign(h.host_id, vm.vm_id)} style={{
                                            padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11,
                                            background: assigning?.hostId === h.host_id && assigning?.vmId === vm.vm_id ? '#374151' : '#1c3a2a',
                                            color: assigning?.hostId === h.host_id && assigning?.vmId === vm.vm_id ? '#d1d5db' : '#6ee7b7',
                                            cursor: 'pointer', fontWeight: 600,
                                          }}>
                                            Assign
                                          </button>
                                        </td>
                                      </tr>
                                      {assigning?.hostId === h.host_id && assigning?.vmId === vm.vm_id && (
                                        <tr>
                                          <td colSpan={7} style={{ padding: '8px 12px', background: '#0f172a' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                              <div style={{ position: 'relative', flex: 1, maxWidth: 260 }}>
                                                <input
                                                  value={assignSearch}
                                                  onChange={e => { setAssignSearch(e.target.value); setAssignUserId(''); setAssignUserLabel(''); setAssignDropdownOpen(true); }}
                                                  onFocus={() => setAssignDropdownOpen(true)}
                                                  onBlur={() => setTimeout(() => setAssignDropdownOpen(false), 150)}
                                                  placeholder={assignUserLabel || 'Search user...'}
                                                  style={{ ...inputStyle, fontSize: 12, padding: '4px 8px', width: '100%', boxSizing: 'border-box' }}
                                                />
                                                {assignDropdownOpen && (
                                                  <div style={{
                                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                                    background: '#1f2937', border: '1px solid #374151', borderRadius: 6,
                                                    maxHeight: 180, overflowY: 'auto', marginTop: 2,
                                                  }}>
                                                    {users
                                                      .filter(u => {
                                                        const q = assignSearch.toLowerCase();
                                                        return !q || (u.username || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q);
                                                      })
                                                      .map(u => (
                                                        <div
                                                          key={u.user_id}
                                                          onMouseDown={() => { setAssignUserId(u.user_id); setAssignUserLabel(`${u.username}${u.name ? ` (${u.name})` : ''}`); setAssignSearch(''); setAssignDropdownOpen(false); setAssignError(null); }}
                                                          style={{
                                                            padding: '6px 10px', cursor: 'pointer', fontSize: 12,
                                                            color: assignUserId === u.user_id ? '#93c5fd' : '#d1d5db',
                                                            background: assignUserId === u.user_id ? '#1e3a5f' : 'transparent',
                                                          }}
                                                          onMouseEnter={e => e.currentTarget.style.background = '#374151'}
                                                          onMouseLeave={e => e.currentTarget.style.background = assignUserId === u.user_id ? '#1e3a5f' : 'transparent'}
                                                        >
                                                          {u.username}{u.name ? <span style={{ color: '#6b7280' }}> ({u.name})</span> : ''}
                                                        </div>
                                                      ))
                                                    }
                                                    {users.filter(u => { const q = assignSearch.toLowerCase(); return !q || (u.username || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q); }).length === 0 && (
                                                      <div style={{ padding: '6px 10px', fontSize: 12, color: '#6b7280' }}>No users found</div>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                              <button onClick={submitAssign} disabled={!assignUserId || assignLoading} style={{
                                                padding: '4px 12px', borderRadius: 4, border: 'none', fontSize: 11, fontWeight: 600,
                                                background: assignSuccess ? '#14532d' : (!assignUserId || assignLoading) ? '#1c3a2a44' : '#1c3a2a',
                                                color: assignSuccess ? '#bbf7d0' : '#6ee7b7',
                                                cursor: !assignUserId || assignLoading ? 'not-allowed' : 'pointer',
                                              }}>
                                                {assignSuccess ? 'Assigned!' : assignLoading ? 'Assigning...' : 'Confirm'}
                                              </button>
                                              <button onClick={() => setAssigning(null)} style={{
                                                padding: '4px 8px', borderRadius: 4, border: 'none', fontSize: 11,
                                                background: 'none', color: '#6b7280', cursor: 'pointer',
                                              }}>✕</button>
                                              {assignError && <span style={{ color: '#fca5a5', fontSize: 11 }}>{assignError}</span>}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </Fragment>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const labelStyle = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelText = { color: '#9ca3af', fontSize: 12 };
const inputStyle = {
  padding: '8px 10px', borderRadius: 6, border: '1px solid #555',
  background: '#1a1a1a', color: '#fff', fontSize: 13, outline: 'none',
};
